import { parseISO } from 'date-fns';

/**
 * Reading DATEONLY columns, which arrive from the API as a bare 'yyyy-MM-dd'
 * string with no time and no zone.
 *
 * DateUtil cannot be used for these: formatFromDB parses the bare string as
 * LOCAL midnight and then re-renders it in UTC, which lands on the previous day
 * for anyone at a non-negative UTC offset. Correct for every US timezone and
 * silently wrong elsewhere — and for a year that means the wrong year every
 * January 1st. A calendar date has no zone, so don't involve one.
 */

/**
 * '2026-03-01' -> '2026'. Returns '' for anything unparseable.
 *
 * The provider's inactive date is stored in full but only ever shown as a year:
 * liaisons know the season someone left, not the day, and a precise-looking
 * date invites a precision the data does not have.
 */
export const yearFromDateOnly = (value) => {
  const match = /^(\d{4})-\d{2}-\d{2}/.exec(String(value || ''));
  return match ? match[1] : '';
};

/**
 * The app's date range ({ start, end }, local-time ISO strings from the picker) as whole
 * UTC calendar days: { from: 'yyyy-MM-ddT00:00:00.000Z', to: 'yyyy-MM-ddT23:59:59.999Z' },
 * using the calendar days the user picked.
 *
 * Use it for every report request that sends the range as from/to for Visits, or for
 * Scorecards/RVUs (the backend turns from/to into UTC months). Visit dates are calendar
 * dates stored at midnight UTC, so sending the picker's local instants shifts the range by
 * the user's UTC offset: in Central time a November range ran Nov 1 05:00Z to Dec 1 05:59Z,
 * which dropped Nov 1 visits, picked up Dec 1 visits, and made those month-based reports
 * read the range's end as December. (ClientScorecard and ProviderScorecard don't need it:
 * they pick months from the range on the client, in local time.)
 *
 * Returns { from: undefined, to: undefined } when there is no range, so callers' existing
 * "if from and to" checks skip the filter as before.
 */
export const utcDayRange = (dateRange) => {
  if (!dateRange || !dateRange.start || !dateRange.end) {
    return { from: undefined, to: undefined };
  }
  const toDate = (value) => (typeof value === 'string' ? parseISO(value) : value);
  const start = toDate(dateRange.start);
  const end = toDate(dateRange.end);
  return {
    from: new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()))
      .toISOString(),
    to: new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999))
      .toISOString(),
  };
};

export default { yearFromDateOnly, utcDayRange };
